import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from '../db/db.js';
import { api } from '../api.js';
import TopBar from '../ui/TopBar.js';

interface Result {
  pageId: string; pageTitle: string; pageDate: string; notebook: string;
  snippet: string; rows: number[];
}

/** Busca local na transcrição que já veio no sync — é o que faz a busca
 *  funcionar dentro da planta, sem rede (SPEC §8). */
async function searchLocally(q: string): Promise<Result[]> {
  const norm = (s: string) => s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
  const needle = norm(q);
  const pages = await db.pages.filter((p) => !p.deletedAt && !!p.transcript).toArray();
  const out: Result[] = [];
  for (const p of pages) {
    const rows: number[] = [];
    let snippet = '';
    for (const line of (p.transcript ?? '').split('\n')) {
      const m = /^L(\d+):\s?(.*)$/.exec(line);
      if (!m) continue;
      if (norm(m[2] ?? '').includes(needle)) {
        rows.push(Number(m[1]));
        if (!snippet) snippet = m[2] ?? '';
      }
    }
    if (rows.length) {
      out.push({ pageId: p.id, pageTitle: p.title, pageDate: p.pageDate, notebook: '', snippet, rows });
    }
  }
  return out.sort((a, b) => (a.pageDate < b.pageDate ? 1 : -1));
}

export default function SearchView(): JSX.Element {
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  const [results, setResults] = useState<Result[]>([]);
  const [offline, setOffline] = useState(false);

  useEffect(() => {
    if (q.trim().length < 2) { setResults([]); return; }
    const id = window.setTimeout(() => {
      void (async () => {
        try {
          const res = await api.get<{ results: Result[] }>(`/api/search?q=${encodeURIComponent(q)}`);
          setResults(res.results);
          setOffline(false);
        } catch {
          setResults(await searchLocally(q));
          setOffline(true);
        }
      })();
    }, 250);
    return () => window.clearTimeout(id);
  }, [q]);

  return (
    <>
      <TopBar title="Buscar" back="/" />
      <div className="content stack">
        <input autoFocus placeholder="pallets, auditoria, Carlos…" value={q} onChange={(e) => setQ(e.target.value)} />
        {offline ? <p className="muted small">Sem rede: buscando só no que já foi transcrito neste aparelho.</p> : null}
        <div className="stack">
          {results.map((r) => (
            <button
              key={r.pageId} className="card"
              onClick={() => navigate(`/p/${r.pageId}#L${r.rows[0] ?? 1}`)}
            >
              <h3>{r.pageTitle || 'Sem título'}</h3>
              <p>{[r.notebook, r.pageDate, r.rows.length ? `linha ${r.rows.join(', ')}` : ''].filter(Boolean).join(' · ')}</p>
              <p className="mono" style={{ marginTop: 6 }}>{r.snippet}</p>
            </button>
          ))}
          {q.trim().length >= 2 && !results.length ? <p className="muted">Nada encontrado.</p> : null}
        </div>
      </div>
    </>
  );
}
